import { BaseLayout } from '@web/features/layouts/base';

export function PrivacyPage() {
  return (
    <BaseLayout>
      <div
        style={{
          maxWidth: '35em',
          backgroundColor: 'rgba(0, 0, 0, .5)',
          padding: '1em',
          margin: 'auto',
        }}
      >
        <h1>DATA PRIVACY POLICY: Applied Love Labs and Time Machine</h1>
        <p>Last Updated and Effective July 5, 2024</p>
        <h2>
          <strong>
            What is a Data Privacy Policy and Why Should You Care?
          </strong>
        </h2>
        <p>
          <strong>Applied Love Labs' approach to privacy and privacy language: </strong>A
          common complaint of data privacy policies is that they are too
          complex, too long, too dense and with wording that makes them hard to
          understand. To avoid this, we keep explanations short, use plain
          language, put information in intuitive sections, and keep legalistic
          explanations to a minimum.
        </p>
        <p>
          In this Privacy Policy, we use action verbs like gather, use, process,
          keep, maintain, share and delete to refer to how your data will be
          acted on. We want to make clear from the start that how your data is
          acted on depends on how you are interacting with the website or apps
          and what feature you are using. We go over this in detail below.. In
          general terms, Applied Love Labs has made a commitment to minimize gathering,
          keeping and sharing your data. But we do engage in all three. We tend
          to use the word &ldquo;process&rdquo; as a default for all three
          actions throughout this policy. We prefer &ldquo;process&rdquo;
          because if your data is gathered it will be processed even if at a
          minimal level, just by you using the app at all. In very rare
          occasions, Applied Love Labs will share your data in a manner that benefits both
          you and Applied Love Labs, but we will only do this with your prior consent. Applied Love Labs
          never sells your data.
        </p>
        <p>
          Why should you care about the treatment of your personal data? This
          may sound obvious, but we all share private information all the time
          &mdash; our addresses, credit card numbers and other account numbers,
          social security numbers, birthdays, and more. But when we share any of
          these with an organization,&nbsp; data privacy policies come into
          play. A good data privacy policy will be transparent and tell users
          exactly how their information will be gathered, used, protected and
          removed. There should be no surprises.&nbsp;
        </p>
        <p>
          At Applied Love Labs and{' '}
          <a
            href="https://www.timemachine.love/"
            target="_blank"
            rel="noreferrer"
          >
            Time Machine
          </a>
          , due to the nature of our work, we store and use people's personal
          stories as a tool for improved mental and emotional well-being. These
          personal stories are likely far more precious than digital
          identification data, so we take data privacy extremely seriously and
          believe that you should as well. Our websites and apps are designed to
          protect the confidentiality of any audio or video messages you may
          choose to record for your future use. If you have any questions while
          reading this policy, please don't hesitate to reach out to{' '}
          <a href="mailto:help@applied.love">help@applied.love</a>.
        </p>
        <h2>What do we mean by data?</h2>
        <p>
          For purposes of this Data Privacy Policy and unless otherwise
          specified, &ldquo;data&rdquo; includes information that is linked to
          one person or a household including things like name, email address,
          login ID, third party identifiers, and contact information. Some
          jurisdictions might consider this to be &ldquo;personal data,&rdquo;
          &ldquo;personally identifiable information,&rdquo; or &ldquo;sensitive
          personal data&rdquo; in certain circumstances. We use Personally
          Identifiable Information (PII) or &ldquo;user data&rdquo; or
          &ldquo;your data.&rdquo; When you use and access our app or website,
          you accept and agree at each data interaction point to both this Data
          Privacy Policy and the Terms and Conditions, including that we will at
          times, and upon obtaining prior consent, share certain data with third
          party service providers (see below).
        </p>
        <h2>Opt-In (and case-by-case) Consent versus Global Consent</h2>
        <p>
          At Applied Love Labs, we have chosen an Opt-in (case-by-case) approach to obtaining
          user consent. At each user interaction when user personal data (PII)
          is gathered, we will request consent from you, the user. While this
          means you may be asked more than once to confirm you're OK with
          sharing your data, we view this approach as transparent and
          respectful..
        </p>
        <h2>This Document will be Updated Periodically</h2>
        <p>
          We will update this Data Privacy Policy from time to time. When we do,
          we will notify you through our website or app when you log in to your
          account. We encourage you to periodically review this page for the
          latest information. The date of the most recent update will be visible
          at the beginning of the Privacy Policy.
        </p>
        <h2>Global Data Privacy Policy</h2>
        <p>
          This privacy policy serves as a global data privacy policy for Applied Love Labs,
          Time Machine and any other websites or application tools that may be
          developed by Applied Love Labs. For each application developed by Applied Love Labs, this data
          privacy policy will be referenced and updated as necessary.
        </p>
        <h2>
          The Specifics on Gathering, Storing, or Processing Your Data
          <strong></strong>
        </h2>
        <p>
          We process data to do things like operate the app platform and make
          sure you are able to use our services effectively. We may also process
          data to send you periodic newsletters. In all cases, the primary
          purpose of any communication is to help provide services to you.
          Infrequently, we may use communications to provide marketing for a
          service or event provided by Applied Love Labs in which we reasonably believe you
          may have interest. But mostly, collecting data allows us to be more
          effective and to better assist more users who may be looking for
          mental health support. In addition, you can opt out of receiving texts
          or marketing communications at any time. Finally, if you opt-in, we
          may share some of your data with third parties that help us provide
          our services. Applied Love Labs never sells data, and we carefully select the
          parties with whom we might share data. If you do not opt-in, we will
          not share your data with third parties.
          <strong></strong>
        </p>
        <h2>What specific data is Applied Love Labs gathering and processing?</h2>
        <p>
          Which data we process depends on how you're using our website or apps,
          and if you opt-in to data sharing. We explain in the section below the
          specific data we collect and process and the categories of information
          that can be shared with third party providers and the result, purpose,
          and, if you consent, the benefit of doing so.
        </p>
        <h2>
          Information Shared with Third Party Providers if You Choose to Use Our
          Tools
        </h2>
        <p>Applied Love Labs utilizes the following Third Party integrations:</p>
        <ul>
          <li>YouTube</li>
          <li>Virtuous</li>
          <li>Stripe</li>
          <li>Fly.io</li>
          <li>Webflow</li>
          <li>Vimeo</li>
        </ul>
        <p>
          We will inform you and request your consent prior to your use of these
          integrations.
        </p>
        <h3>
          <em>YouTube</em> (Video creation and hosting)
          <strong></strong>
        </h3>
        <p>
          At Applied Love Labs, in keeping with our commitment to value your PII, when we use
          links to <em>YouTube</em> videos, we use a privacy-conscious{' '}
          <em>YouTube</em> &ldquo;no cookie&rdquo; integration which means that
          you aren't tracked by <em>YouTube</em> when you load a{' '}
          <em>YouTube</em> video on our website. Typically, by interacting with{' '}
          <em>YouTube</em> videos, <em>YouTube</em> would send a cookie but by
          using the &ldquo;no cookie&rdquo; integration, you are not tracked.
        </p>
        <p>
          To be extra careful, we still feel that you should review the Data
          Privacy Policy of <em>YouTube</em>: (
          <a
            href="https://www.youtube.com/howyoutubeworks/our-commitments/protecting-user-data/"
            target="_blank"
            rel="noreferrer"
          >
            https://www.youtube.com/howyoutubeworks/our-commitments/protecting-user-data/
          </a>
          ).
        </p>
        <h3>
          <em>Virtuous</em> (CRM software)
        </h3>
        <p>
          At Applied Love Labs, we use <em>Virtuous</em> as our Customer Relationship
          Management Software (CRM).
          <em>Virtuous</em> helps us to manage our newsletter signups as well as
          donations made to Applied Love Labs. There is no personal data shared with{' '}
          <em>Virtuous</em> when a linked form is loaded, but data is passed to{' '}
          <em>Virtuous</em> when you hit &ldquo;Send&rdquo; to sign up for the
          newsletter or to donate. For the newsletter, we save your email
          address and name to send personalized emails. Prior to agreeing to
          receive our newsletter, we will ask for specific consent to process
          user information in order to receive our newsletter. This is an
          example of sharing data with a partner in order for proper functioning
          of our services, and this is what we mean by &ldquo;opt-in&rdquo;
        </p>
        <p>
          The sharing process is governed by this policy and the{' '}
          <em>Virtuous</em> privacy policy (
          <a
            href="https://virtuous.org/software/gdpr-compliance/"
            target="_blank"
            rel="noreferrer"
          >
            https://virtuous.org/software/gdpr-compliance/
          </a>
          ). It is our understanding that <em>Virtuous</em> only uses your
          information as an agent of Applied Love Labs but in order to be fully comfortable,
          you should review the <em>Virtuous</em> privacy policy.
        </p>
        <h3>
          <em>Stripe</em> (Applied Love Labs payment processing component integration)
        </h3>
        <p>
          Applied Love Labs uses <em>Stripe</em> as the actual payment processor for
          donations. Given the seriousness of sharing financial information in
          general, payment processing is a highly regulated industry since a
          great deal more sensitive data is processed than, say, for a
          newsletter sign-up. Applied Love Labs only uses the <em>Stripe</em> integration on
          the donations page, but the <em>Stripe</em> donations page does
          request and process sensitive financial data, primarily for
          fraud-prevention purposes. Applied Love Labs does not send any PII information
          related to Applied Love Labs well-being tools. But given that this is the payment
          processing software, <em>Stripe</em> will request name, credit card
          number and the like in order to process donations and all of your data
          linked to donations will be transferred to <em>Stripe</em>. This
          information will only be used to process the donation.
        </p>
        <p>
          Applied Love Labs requests explicit consent before loading the <em>Stripe</em>{' '}
          integration on the donation page. This is another example of
          &ldquo;opt-in.&rdquo;
        </p>
        <p>
          Once again, out of an abundance of caution, we still feel that you
          should review the Data Privacy Policy of <em>Stripe</em>: (
          <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
            https://stripe.com/privacy
          </a>
          ).
        </p>
        <h3>
          <em>Fly.io</em> (Time Machine Application Cloud Provider)
        </h3>
        <p>
          The Applied Love Labs Time Machine application is hosted on <em>Fly.io</em>. It is
          the cloud computing provider where the Time Machine application is
          stored.All information including application codes are stored here.{' '}
          <em>Fly.io</em> safeguards (encrypts) all application information and
          no information is shared by <em>Fly.io </em>with third parties.
        </p>
        <p>
          <em>Fly.io</em> clearly states that it &ldquo;
          <em>does not sell, share, rent, or trade user PII.</em>&rdquo; Please
          see <em>Fly.io</em> data privacy policy here: (
          <a
            href="https://fly.io/legal/privacy-policy/"
            target="_blank"
            rel="noreferrer"
          >
            https://fly.io/legal/privacy-policy/
          </a>
          ).
        </p>
        <h3>
          <em>Webflow.com</em> (Time Machine website host)
        </h3>
        <p>
          The Time Machine website is hosted on <em>Webflow.com</em>. It is
          similar to numerous other website creation and hosting locations. As a
          website host, no user PII data is stored with <em>Webflow.com</em>.
          <em></em>
        </p>
        <p>
          <em>Webflow.com</em> safeguards (encrypts) all information required to
          create and host websites and no information is shared with third
          parties by <em>Webflow.com</em>.
        </p>
        <p>
          <em>Webflow.com</em> clearly states that it &ldquo;
          <em>
            will not give, sell, rent, or loan any Personal Information to any
            third party
          </em>
          .&rdquo;Please see <em>Webflow.com</em> data privacy policy here: (
          <a
            href="https://webflow.com/legal/privacy"
            target="_blank"
            rel="noreferrer"
          >
            https://webflow.com/legal/privacy
          </a>
          ).
        </p>
        <h3>Vimeo (Video hosting and sharing)</h3>
        <p>
          <em>Vimeo</em> is similar to <em>YouTube</em>, however, unlike{' '}
          <em>YouTube</em>, <em>Vimeo</em> does not track you through the{' '}
          <em>Vimeo</em> video player. This means that by interacting with the{' '}
          <em>Vimeo</em> videos on Applied Love Labs, you are not tracked.
        </p>
        <p>
          To be extra careful, we still feel that users should review the Data
          Privacy Policy of <em>Vimeo</em>, found here: (
          <a href="https://vimeo.com/privacy" target="_blank" rel="noreferrer">
            https://vimeo.com/privacy
          </a>
          ).
        </p>
        <h2>Opting Out and Removing Your Personal Data</h2>
        <p>
          You can opt-out (request deletion an opt-out) of any and all Applied Love Labs
          services simply by sending a deletion request to: team@applied.love.
        </p>
        <p>
          When Applied Love Labs receives a deletion request from you, you will receive a
          general acknowledgement within two weeks. At Applied Love Labs, it will generally
          take six weeks for us to thoroughly run through the deletion process.
          This is mainly due to the fact that certain third party providers only
          have periodic purging. If you do not opt-out, information may be
          stored indefinitely.
        </p>
        <p>
          <strong>Clarification for California Users</strong>
        </p>
        <p>
          As we have indicated above, we do not sell user data. In California,
          however, the laws define &ldquo;sale&rdquo; broadly to include the
          sharing of personal information in exchange for anything of value. If
          you opt-in to or use any of our services with third party providers,
          this use may be considered a &ldquo;sale&rdquo; of personal
          information under California law. For specific information on your
          data rights as a resident of California, see the California Consumer
          Privacy Act (CCPA), here
          <a
            href="https://oag.ca.gov/privacy/ccpa"
            target="_blank"
            rel="noreferrer"
          >
            {' '}
            https://oag.ca.gov/privacy/ccpa
          </a>{' '}
          and here{' '}
          <a href="https://cppa.ca.gov/faq" target="_blank" rel="noreferrer">
            https://cppa.ca.gov/faq
          </a>
          , and the California Privacy Rights Act (CPRA) here
          <a
            href="https://www.caprivacy.org/cpra-resource-center/"
            target="_blank"
            rel="noreferrer"
          >
            {' '}
            https://www.caprivacy.org/cpra-resource-center/
          </a>
          .
        </p>
        <p>
          The CCPA and the CPRA are California laws that provide its residents
          with certain rights over information about them, including notice
          about the categories of personal information we have collected from
          them in the preceding twelve (12) months and the purposes for which
          the information is used or disclosed. We have indicated above the user
          data that is processed by Applied Love Labs, as well as the purpose for collection.
        </p>
      </div>
    </BaseLayout>
  );
}

export default PrivacyPage;
